import Cookies from 'universal-cookie';

// Global reference to getAccessTokenSilently function
// This allows us to use the SDK method outside React components (e.g., in baseQuery)
// Used as fallback when localStorage token is expired
let getTokenFunction = null;

export const setAuth0TokenGetter = (getTokenFn) => {
	getTokenFunction = getTokenFn;
};

/**
 * Clears all Auth0 cache entries from localStorage
 * Used when refresh token is missing/invalid to force a fresh login
 */
export const clearAuth0Cache = () => {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);
		auth0CacheKeys.forEach(key => {
			localStorage.removeItem(key);
		});
	} catch (error) {
		// Ignore cache cleanup issues.
	}
};

/**
 * Reads access token directly from Auth0 localStorage cache
 * This is synchronous and available immediately on page load
 * @returns {string|null} Access token if found, null otherwise
 */
export const getTokenFromLocalStorage = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);

		for (const key of auth0CacheKeys) {
			try {
				const cacheValue = localStorage.getItem(key);
				if (cacheValue) {
					const cacheData = JSON.parse(cacheValue);
					// Auth0 stores access_token in cache.body
					if (cacheData?.body?.access_token) {
						const token = cacheData.body.access_token;
						
						// Check if token is expired (JWT has exp claim)
						try {
							const payload = JSON.parse(atob(token.split('.')[1]));
							const now = Math.floor(Date.now() / 1000);
							if (payload.exp && payload.exp > now) {
								// Token is valid
								return token;
							}
							// Token is expired - will need refresh
							return null;
						} catch (e) {
							// If we can't parse the token, assume it's valid and let the API decide
							return token;
						}
					}
				}
			} catch (e) {
				continue;
			}
		}
	} catch (error) {}

	return null;
};

/**
 * Reads ID token directly from Auth0 localStorage cache
 * This is synchronous and available immediately on page load
 * @returns {string|null} ID token if found, null otherwise
 */
export const getIdTokenFromLocalStorage = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);

		for (const key of auth0CacheKeys) {
			try {
				const cacheValue = localStorage.getItem(key);
				if (cacheValue) {
					const cacheData = JSON.parse(cacheValue);
					
					// Auth0 stores id_token in cache.body (most common location)
					if (cacheData?.body?.id_token) {
						return cacheData.body.id_token;
					}
					
					// Check alternative locations (some SDK versions store differently)
					if (cacheData?.id_token) {
						return cacheData.id_token;
					}
					
					// Check if stored in a nested structure
					if (cacheData?.cache?.id_token) {
						return cacheData.cache.id_token;
					}
				}
			} catch (e) {
				continue;
			}
		}
	} catch (error) {
		// Silently fail - token may not be available
	}

	return null;
};

/**
 * Gets Auth0 access token, trying localStorage first, then SDK method
 * @returns {Promise<string|null>} Access token if available, null otherwise
 */
export const getAuth0Token = async () => {
	// First, try to get token directly from localStorage (synchronous, immediate)
	const localStorageToken = getTokenFromLocalStorage();
	if (localStorageToken) {
		return localStorageToken;
	}

	// If no token in localStorage or it's expired, try SDK method (handles refresh)
	if (getTokenFunction) {
		try {
			const token = await getTokenFunction({
				audience: 'https://portal-api'
			});
			return token;
		} catch (error) {
			if (error.error === 'login_required' || 
				error.message?.includes('login_required') ||
				error.message?.includes('Login required')) {
				return null;
			}
			
			// If refresh token is missing, clear stale Auth0 cache so app knows user is not authenticated
			// This triggers a proper login flow instead of redirect loops
			if (error.message?.includes('Missing Refresh Token')) {
				clearAuth0Cache();
				return null;
			}
			
			return null;
		}
	}

	// SDK not ready yet - return null (API call will proceed without token)
	return null;
};

/**
 * Sets the binf_auth_cookie that IBP uses for authentication
 * Cookie domain is set to localhost for localhost, metabolon.com for other environments
 * @param {string} token - The Auth0 access token to set in the cookie
 */
export const setBinfAuthCookie = (token) => {
	if (typeof window === 'undefined' || !token) {
		return false;
	}
	
	try {
		const domain = window.location.hostname === 'localhost' ? 'localhost' : 'metabolon.com';
		const isSecureContext = window.location.protocol === 'https:';
		const db = new Cookies();
		
		db.set('binf_auth_cookie', token, {
			path: '/',
			sameSite: 'strict',
			domain: domain,
			secure: isSecureContext
		});
		return true;
	} catch (error) {
		return false;
	}
};

/**
 * Navigates to IBP
 * Cookie is managed globally in App.jsx, so no need to set it here
 * @param {string} url - The IBP URL to navigate to
 * @returns {void}
 */
export const navigateToIBP = (url) => {
	// Navigate using anchor with rel="noreferrer" to prevent Referer header
	const link = document.createElement('a');
	link.href = url;
	link.rel = 'noreferrer';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

